import { useState, useEffect } from 'react'
import {
  getActionsForEntity,
  runAgent,
  approveAction,
  rejectAction,
  type AgentActionApi,
  type AgentType,
} from '@/services/api/agents'

interface AIRecommendationsPanelProps {
  entityType: 'shipment' | 'clearing'
  entityId: string
  agentType: AgentType
  onActionProcessed?: () => void
}

const riskLevelColor: Record<string, string> = {
  low: '#13AB5B',
  medium: '#E6A23C',
  high: '#E93323',
  critical: '#B91C1C',
}

export default function AIRecommendationsPanel({
  entityType,
  entityId,
  agentType,
  onActionProcessed,
}: AIRecommendationsPanelProps) {
  const [actions, setActions] = useState<AgentActionApi[]>([])
  const [loading, setLoading] = useState(false)
  const [running, setRunning] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [processingId, setProcessingId] = useState<string | null>(null)

  const fetchActions = async () => {
    if (!entityId) return
    setLoading(true)
    setError(null)
    try {
      const list = await getActionsForEntity(entityType, entityId)
      setActions(list)
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to load recommendations'
      setError(msg)
      setActions([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchActions()
  }, [entityType, entityId])

  const handleRunAgent = async () => {
    setRunning(true)
    setError(null)
    try {
      await runAgent(agentType, entityId)
      await fetchActions()
      onActionProcessed?.()
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: { message?: string } } }; message?: string }
      const msg = err?.response?.data?.error?.message || err?.message || 'Failed to run agent'
      setError(msg)
    } finally {
      setRunning(false)
    }
  }

  const handleApprove = async (actionId: string) => {
    setProcessingId(actionId)
    setError(null)
    try {
      await approveAction(actionId)
      await fetchActions()
      onActionProcessed?.()
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to approve'
      setError(msg)
    } finally {
      setProcessingId(null)
    }
  }

  const handleReject = async (actionId: string, reason?: string) => {
    setProcessingId(actionId)
    setError(null)
    try {
      await rejectAction(actionId, reason)
      await fetchActions()
      onActionProcessed?.()
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to reject'
      setError(msg)
    } finally {
      setProcessingId(null)
    }
  }

  const runSummary = actions[0]?.agentRun?.aiOutput as { summary?: string; reasoning?: string; riskLevel?: string } | undefined
  const riskLevel = runSummary?.riskLevel ?? 'low'
  const summary = runSummary?.summary ?? ''
  const reasoning = runSummary?.reasoning ?? ''

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900">AI Recommendations</h3>
        <button
          type="button"
          disabled={running || !entityId}
          onClick={handleRunAgent}
          className="px-3 py-1.5 rounded text-sm font-medium bg-gray-600 text-white hover:bg-gray-700 disabled:opacity-50"
          title="Recommendations are also generated automatically when you create or update this record."
        >
          {running ? 'Running…' : 'Re-analyze'}
        </button>
      </div>
      {error && (
        <p className="text-red-600 text-xs mb-2">{error}</p>
      )}
      {loading && <p className="text-gray-500 text-xs">Loading…</p>}
      {!loading && actions.length === 0 && !error && (
        <p className="text-gray-500 text-xs">Recommendations appear here when the system analyzes this record (e.g. after you create or update it). Use &quot;Re-analyze&quot; to run analysis now, or see AI Actions for all pending items.</p>
      )}
      {!loading && actions.length > 0 && (
        <>
          {(reasoning || summary) && (
            <p className="text-gray-700 text-xs mb-2">{reasoning || summary}</p>
          )}
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs text-gray-500">Risk:</span>
            <span
              className="text-xs font-medium"
              style={{ color: riskLevelColor[riskLevel] || riskLevelColor.low }}
            >
              {riskLevel}
            </span>
          </div>
          <ul className="space-y-2">
            {actions.map((action) => {
              const desc = (action.payload?.description ?? action.payload?.message) as string | undefined
              return (
              <li
                key={action.id}
                className="flex items-center justify-between text-xs border-b border-gray-100 pb-2 last:border-0"
              >
                <span className="text-gray-700">
                  {desc?.trim() || `${action.actionType}${action.target ? ` → ${action.target}` : ''}`}
                </span>
                <div className="flex gap-1">
                  <button
                    type="button"
                    disabled={processingId === action.id}
                    onClick={() => handleApprove(action.id)}
                    className="px-2 py-1 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 text-xs"
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    disabled={processingId === action.id}
                    onClick={() => handleReject(action.id)}
                    className="px-2 py-1 rounded bg-gray-500 text-white hover:bg-gray-600 disabled:opacity-50 text-xs"
                  >
                    Reject
                  </button>
                </div>
              </li>
              )
            })}
          </ul>
          <p className="text-xs text-gray-500 mt-2">
            This is an AI-generated recommendation. Please verify before approving.
          </p>
        </>
      )}
    </div>
  )
}
